import type { FormInstance } from "antd";
import { Button, Form, InputNumber, Modal } from "antd";
import { useEffect, useRef, useState } from "react";

type VehicleRatesModalProps = {
  form: FormInstance;
  isOpen: boolean;
  onClose: () => void;
  currentVehicleIndex: number | null;
  currencies: string[];
};

const VehicleRatesModal = ({ form, isOpen, onClose, currentVehicleIndex, currencies }: VehicleRatesModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const initialRatesRef = useRef<Record<string, number | undefined> | null>(null);

  useEffect(() => {
    if (isOpen && currentVehicleIndex !== null && initialRatesRef.current === null) {
      const initialRates = form.getFieldValue(["vehicles", currentVehicleIndex, "rate"]) as
        | Record<string, number | undefined>
        | undefined;
      initialRatesRef.current = { ...initialRates };
    }
  }, [currentVehicleIndex, form, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      setHasValidationErrors(false);
      initialRatesRef.current = null;
    }
  }, [isOpen]);

  if (currentVehicleIndex === null) {
    return null;
  }

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const paths = currencies.map((c) => ["vehicles", currentVehicleIndex, "rate", c]);
      await form.validateFields(paths);
      setHasValidationErrors(false);
      onClose();
    } catch {
      setHasValidationErrors(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscard = () => {
    if (currentVehicleIndex !== null && initialRatesRef.current) {
      currencies.forEach((currency) => {
        form.setFieldValue(["vehicles", currentVehicleIndex, "rate", currency], initialRatesRef.current?.[currency]);
      });
    }
    onClose();
  };

  return (
    <Modal
      title={
        <div className="space-y-1">
          <div className="text-lg font-semibold">Configure Vehicle Rates</div>
          <div className="text-sm text-gray-500">Set base standard for this vehicle type</div>
        </div>
      }
      open={isOpen}
      width={600}
      onCancel={handleDiscard}
      footer={[
        <Button key="discard" onClick={handleDiscard}>
          Discard
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={isSubmitting}>
          {isSubmitting ? "Saving..." : "Done"}
        </Button>,
      ]}
    >
      {hasValidationErrors && (
        <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
          <strong>Some rate details need attention.</strong> Please fill in all required fields and ensure positive
          numbers.
        </div>
      )}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currencies.map((currency) => (
            <div key={currency} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">{currency} Rate</label>
              <Form.Item
                name={["vehicles", currentVehicleIndex, "rate", currency]}
                rules={[
                  { required: true, message: "Enter rate" },
                  { type: "number", min: 0 },
                ]}
                className="mb-0!"
              >
                <InputNumber<number>
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(displayValue) => {
                    const numericValue = Number((displayValue ?? "").toString().replace(/\$\s?|(,*)/g, ""));
                    return Number.isNaN(numericValue) ? 0 : numericValue;
                  }}
                  placeholder="0"
                  className="!w-full"
                  prefix={currency}
                  min={0}
                />
              </Form.Item>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default VehicleRatesModal;
